import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CandidateAchievements from "../components/CandidateAchievements";
import CandidateOwnershipModal from "../components/CandidateOwnershipModal";
import ClaimOwnershipCard from "../components/ClaimOwnershipCard";
import { useAuth } from "../context/AuthContext";
import { candidates, getImageUrl } from "../services/api";

const POST_LOGIN_CLAIM_KEY = "candidate-claim-return";

const CandidateProfile = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [myClaims, setMyClaims] = useState([]);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const response = await candidates.getByName(name);
        setCandidate(response.data);
      } catch (error) {
        console.error("Failed to load candidate:", error);
        toast.error("Failed to load candidate profile");
      }
    };

    fetchCandidate();
  }, [name]);

  useEffect(() => {
    const fetchMyClaims = async () => {
      if (!isAuthenticated) {
        setMyClaims([]);
        return;
      }

      try {
        const response = await candidates.getMyClaims();
        console.log("[CandidateProfile] getMyClaims success", response.data);
        setMyClaims(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to load claim history:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
        });
      }
    };

    fetchMyClaims();
  }, [isAuthenticated]);

  const isOwnedByCurrentUser = useMemo(() => {
    const currentCandidateId = user?.candidateProfile?._id || user?.candidateProfile;
    return Boolean(currentCandidateId && candidate?.id && String(currentCandidateId) === String(candidate.id));
  }, [candidate, user]);

  const existingClaim = useMemo(() => {
    if (!candidate) {
      return null;
    }

    return (
      myClaims.find((claim) => {
        const sameCandidateId =
          claim.candidateId?._id &&
          String(claim.candidateId._id) === String(candidate.id || candidate._id);
        const sameFingerprint =
          claim.fullName?.toLowerCase() === candidate.name?.toLowerCase() &&
          claim.position?.toLowerCase() === candidate.position?.toLowerCase() &&
          claim.constituency?.toLowerCase() === candidate.constituency?.toLowerCase() &&
          claim.state?.toLowerCase() === candidate.state?.toLowerCase();

        return sameCandidateId || sameFingerprint;
      }) || null
    );
  }, [candidate, myClaims]);

  useEffect(() => {
    if (!candidate || !isAuthenticated) {
      return;
    }

    const storedTarget = sessionStorage.getItem(POST_LOGIN_CLAIM_KEY);
    if (storedTarget && storedTarget === candidate.name) {
      sessionStorage.removeItem(POST_LOGIN_CLAIM_KEY);
      setClaimModalOpen(true);
    }
  }, [candidate, isAuthenticated]);

  const handleClaimClick = () => {
    if (candidate?.isClaimed) {
      toast.info("This candidate profile has already been claimed.");
      return;
    }

    if (!isAuthenticated) {
      toast.info("Please log in to claim this candidate profile.");
      sessionStorage.setItem(POST_LOGIN_CLAIM_KEY, candidate.name);
      navigate("/login", { state: { from: location } });
      return;
    }

    setClaimModalOpen(true);
  };

  if (!candidate) {
    return <div className="py-20 text-center text-gray-600">Loading candidate profile...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-green-50 px-4 py-10">
      <div className="mx-auto flex max-w-5xl flex-col items-center rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
        <div className="flex flex-col items-center gap-6 md:flex-row">
          <img
            src={getImageUrl(candidate.image_url, "image/default-avatar.png")}
            onError={(event) => {
              event.currentTarget.src = getImageUrl("image/default-avatar.png");
            }}
            alt={candidate.name}
            className="h-32 w-32 rounded-full border-4 border-green-600 object-cover shadow-md"
          />
          <div className="text-center md:text-left">
            <h1 className="flex items-center gap-2 text-3xl font-bold text-gray-800">
              {candidate.name}
              {candidate.verified && (
                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                  Verified
                </span>
              )}
              {candidate.isClaimed && !candidate.verified && (
                <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-700">
                  Claim Pending
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-600">State: {candidate.state || "Unknown"}</p>
            <p className="text-sm text-gray-600">
              Constituency: {candidate.constituency || "Unknown"}
            </p>
            <p className="text-sm text-gray-500">
              District: {candidate.district || "Not available"}
            </p>
            <p className="text-sm font-semibold text-blue-700">
              {candidate.party || "Unknown Party"}
            </p>
            {candidate.previous_parties?.length > 0 && (
              <p className="mt-1 text-xs text-red-600">
                Switched from: {candidate.previous_parties.join(", ")}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/raise-issue?state=${encodeURIComponent(
                  candidate.state || "",
                )}&constituency=${encodeURIComponent(
                  candidate.constituency || "",
                )}&to=${candidate.position.toLowerCase()}&name=${encodeURIComponent(
                  candidate.name,
                )}`,
              )
            }
            className="rounded bg-yellow-500 px-6 py-2 text-lg text-white shadow-md hover:bg-yellow-600"
          >
            Raise Issue Against
          </button>
        </div>
      </div>

      <div className="mx-auto mt-10 max-w-5xl space-y-8">
        <section>
          <h2 className="mb-4 text-2xl font-bold text-gray-700">Assets & Liabilities</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-lg border bg-green-50 p-5">
              <p className="text-sm text-gray-600">Total Assets</p>
              <p className="text-2xl font-semibold text-green-800">
                {candidate.assets_in_crores ? `Rs ${candidate.assets_in_crores} Cr` : "Not Declared"}
              </p>
            </div>
            <div className="rounded-lg border bg-red-50 p-5">
              <p className="text-sm text-gray-600">Total Liabilities</p>
              <p className="text-2xl font-semibold text-red-600">
                {candidate.liabilities_in_crores
                  ? `Rs ${candidate.liabilities_in_crores} Cr`
                  : "Not Declared"}
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-bold text-gray-700">Issues Raised</h2>
          {candidate.issues?.length > 0 ? (
            <div className="space-y-4">
              {candidate.issues.map((issue, index) => (
                <div key={index} className="rounded-lg border bg-white p-4 shadow-sm">
                  <h3 className="text-lg font-bold text-blue-700">{issue.title}</h3>
                  <p className="text-sm text-gray-600">{issue.description}</p>
                  <span
                    className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                      issue.status === "Resolved"
                        ? "bg-green-600 text-white"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {issue.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No issues raised yet.</p>
          )}
        </section>

        {candidate.achievements?.length > 0 && (
          <section>
            <CandidateAchievements achievements={candidate.achievements} />
          </section>
        )}

        {candidate.rating > 0 && (
          <section>
            <h2 className="mb-4 text-2xl font-bold text-gray-700">Community Rating</h2>
            <p className="text-sm text-gray-600">Average Rating: {candidate.rating}/5</p>
          </section>
        )}

        <section>
          <ClaimOwnershipCard
            candidateName={candidate.name}
            isClaimed={candidate.isClaimed}
            isOwnedByCurrentUser={isOwnedByCurrentUser}
            hasPendingClaim={existingClaim?.status === "pending"}
            onClaim={handleClaimClick}
            onManage={() => navigate("/candidate-dashboard")}
          />
        </section>
      </div>

      <CandidateOwnershipModal
        candidate={candidate}
        existingClaim={existingClaim}
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        onSuccess={(claim) => {
          setMyClaims((current) => [claim, ...current]);
          setCandidate((current) =>
            current
              ? {
                  ...current,
                  isClaimed: true,
                  claimStatus: "pending",
                }
              : current,
          );
        }}
      />
    </div>
  );
};

export default CandidateProfile;
